import React, { useEffect, useState } from "react";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { tryNativeShare, formatConditionLine, shortAgo } from "@/lib/share";
import SharePopupMenu from "@/components/SharePopupMenu";

const CONDITION_DOT = {
    perfect: "🟢", dry: "🟢", dusty: "🟡", humid: "🔵", wet: "🔵",
    mud: "🟠", leaves: "🟡", overgrown: "🟡", broken: "🔴",
};

/**
 * Trails-list button: fetches the latest report for every trail and shares
 * (or shows a fallback popup with) a multi-line summary.
 */
export default function ShareAllTrailsStatusButton({ trails, className = "" }) {
    const { t, lang } = useLang();
    const [latestMap, setLatestMap] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/trail-reports/latest-all")
            .then((r) => { if (alive) setLatestMap(r.data || {}); })
            .catch(() => { if (alive) setLatestMap({}); });
        return () => { alive = false; };
    }, []);

    const hasAny = latestMap && Object.keys(latestMap).length > 0;

    const buildMessage = () => {
        const lines = [];
        for (const tr of trails || []) {
            const r = latestMap?.[tr.slug];
            if (!r) continue;
            const name = (lang === "en" && tr.name_en) ? tr.name_en : tr.name;
            const conditionLabel = t(`reports.conditions.${r.condition}`);
            const when = shortAgo(r.created_at, lang);
            const dot = CONDITION_DOT[r.condition] || "•";
            lines.push(formatConditionLine({
                name, conditionLabel, rating: r.rating,
                userName: r.user_name, when, dot,
            }));
        }
        const header = `🚵 ${t("reports.shareTitleAll").toUpperCase()}`;
        const url = `${window.location.origin}/trail`;
        const text = `${header}\n${t("reports.shareIntroAll")}\n\n${lines.join("\n")}\n\n${t("reports.shareViewAll")} →`;
        return { text, url, count: lines.length };
    };

    const onClick = async () => {
        if (!hasAny) {
            toast.error(t("reports.shareNoData"));
            return;
        }
        const { text, url, count } = buildMessage();
        if (count === 0) {
            toast.error(t("reports.shareNoData"));
            return;
        }
        const result = await tryNativeShare({
            title: t("reports.shareTitleAll"),
            text,
            url,
        });
        if (!result.used) setOpen((o) => !o);
    };

    const message = hasAny ? buildMessage() : null;

    return (
        <div className={`relative inline-block ${className}`}>
            <button
                type="button"
                onClick={onClick}
                disabled={latestMap === null}
                data-testid="share-all-trails-status"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold border-2 border-abnormal-acid text-abnormal-acid hover:bg-abnormal-acid hover:text-black transition-colors px-4 py-3 disabled:opacity-60"
            >
                {latestMap === null ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                {t("reports.shareAll")}
                {hasAny && (
                    <span className="ml-1 px-2 py-0.5 bg-abnormal-acid text-black font-display text-xs">
                        {Object.keys(latestMap).length}
                    </span>
                )}
            </button>
            {open && message && (
                <SharePopupMenu
                    text={message.text}
                    url={message.url}
                    title={t("reports.shareTitleAll")}
                    onClose={() => setOpen(false)}
                    testidPrefix="share-all-trails"
                />
            )}
        </div>
    );
}
